import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useNavigate } from 'react-router-dom';
import { Star, Award, Users, Sparkles, ChevronRight, Play } from 'lucide-react';
import Navbar from '../components/Navbar';
import { useState, useRef, useEffect } from 'react';
import { FreeTrialBanner } from '../components/FreeTrialCTA';
import { siteMediaAPI } from '../lib/api';
import SEO from '../components/common/SEO';

const About = () => {
  const navigate = useNavigate();
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const videoRef = useRef(null);
  const [siteMedia, setSiteMedia] = useState({});

  // Fetch site media settings
  useEffect(() => {
    const fetchSiteMedia = async () => {
      try {
        const res = await siteMediaAPI.getPageSettings('about');
        const mediaMap = {};
        res.data.forEach(item => {
          mediaMap[item.section] = item;
        });
        setSiteMedia(mediaMap);
      } catch (error) {
        console.log('Using default media');
      }
    };
    fetchSiteMedia();
  }, []);

  const teamExperience = [
    'Teaching & Choreography',
    'West End Musicals',
    'UK & International Tours',
    'Television (Soaps & Reality Shows)',
    'Film & Theatre',
    'Pantomime & Cruise Ships',
    'Corporate Entertainment',
  ];

  const handlePlayVideo = () => {
    setIsVideoPlaying(true);
    if (videoRef.current) {
      videoRef.current.play();
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <SEO page="about" />
      <Navbar />

      {/* Hero */}
      <section className="relative py-20 overflow-hidden bg-[#0a0a0a]">
        <div className="absolute inset-0 bg-gradient-to-br from-[#7000FF]/20 via-transparent to-[#FF007F]/20" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="font-accent text-base tracking-[0.3em] text-[#FF007F] uppercase">About UPA · Theatre School in Milton Keynes</span>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold uppercase mt-4 mb-6">
                UNIVERSAL <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FF007F] to-[#7000FF]">PERFORMING ARTS</span>
              </h1>
              <p className="text-xl text-[#FFD700] font-accent mb-4">
                "Believe, learn, achieve, together we are Universal!"
              </p>
              <p className="text-white/70 leading-relaxed">
                UPA is a specialist theatre school in Milton Keynes for ages 4–18,
                offering weekly musical theatre, acting, singing and dance training
                taught by West End and TV professionals. Saturday classes at
                Kents Hill (MK7&nbsp;6BZ); weekday classes at Watling Academy,
                Whitehouse (MK8&nbsp;1EP).
              </p>
            </div>
            <div className="relative flex items-center justify-center h-full">
              <img 
                src={siteMedia.hero?.url 
                  ? (siteMedia.hero.url.startsWith('/api/') 
                      ? `${process.env.REACT_APP_BACKEND_URL}${siteMedia.hero.url}` 
                      : siteMedia.hero.url)
                  : "https://customer-assets.emergentagent.com/job_1d578f26-1f3f-468c-870b-210440301216/artifacts/91xm8p9p_58334bed-790a-461a-8267-9ac573ab5d13.png"
                } 
                alt={siteMedia.hero?.alt_text || "UPA School"}
                className="w-full h-full object-contain"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Vision, Mission, Aim */}
      <section className="py-20 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="bg-[#111] border-[#333] hover:border-[#FF007F]/50 transition-colors">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-[#FF007F]/10 border border-[#FF007F]/30 flex items-center justify-center mb-4">
                  <Star className="w-6 h-6 text-[#FF007F]" />
                </div>
                <h3 className="font-heading text-xl font-bold uppercase mb-3 text-[#FF007F]">Our Vision</h3>
                <p className="text-white/70">
                  To bring not only skill, but also enjoyment to our students, ensuring that they 
                  have the confidence and ability to succeed both in the industry and in life.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-[#111] border-[#333] hover:border-[#7000FF]/50 transition-colors">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-[#7000FF]/10 border border-[#7000FF]/30 flex items-center justify-center mb-4">
                  <Award className="w-6 h-6 text-[#7000FF]" />
                </div>
                <h3 className="font-heading text-xl font-bold uppercase mb-3 text-[#7000FF]">Our Mission</h3>
                <p className="text-white/70">
                  To bring the skill of Performing Arts to this region at a whole new level. 
                  We set goals for our students to achieve, giving parents the opportunity to 
                  monitor their child's progress.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-[#111] border-[#333] hover:border-[#FFD700]/50 transition-colors">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-[#FFD700]/10 border border-[#FFD700]/30 flex items-center justify-center mb-4">
                  <Sparkles className="w-6 h-6 text-[#FFD700]" />
                </div>
                <h3 className="font-heading text-xl font-bold uppercase mb-3 text-[#FFD700]">Our Aim</h3>
                <p className="text-white/70">
                  To impart foundation skills and develop natural talents into well-rounded 
                  performers, ready to take their first steps into the business in a professional 
                  and positive manner.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Principal Section */}
      <section className="py-20 bg-gradient-to-r from-[#7000FF] to-[#FF007F]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <span className="font-accent text-base tracking-[0.3em] text-[#FFD700] uppercase">Principal</span>
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold uppercase mt-2 mb-6">Sam Attwater</h2>
              <div className="space-y-4 text-white/90">
                <p>
                  Sam trained at the Italia Conti Academy of Theatre Arts. He landed his first 
                  television role playing Ricky in Hollyoaks in 2009. In January 2010 he joined 
                  the cast of EastEnders, playing the character Leon Small.
                </p>
                <p>
                  Sam was the winner of the sixth series of ITV1's Dancing on Ice in March 2011. 
                  He also went on to win the tour where he skated alongside Torvill & Dean.
                </p>
                <p className="text-white/70 text-sm">
                  <strong className="text-[#FFD700]">Theatre Credits:</strong> Rags (Park Theatre London), 
                  End of the Rainbow (UK tour), Dreamboats and Petticoats (West End), 
                  The Rocky Horror Show (40th anniversary UK Tour), Seven Brides for Seven Brothers, 
                  Tonight's the Night, and many more.
                </p>
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <div className="rounded-xl overflow-hidden border-4 border-[#FFD700] shadow-2xl">
                <img 
                  src="https://customer-assets.emergentagent.com/job_b293e9ea-1983-4cbc-af83-015ce5ba04ed/artifacts/q9cu5neh_sam%20.jpg" 
                  alt="Sam Attwater" 
                  className="w-full h-96 object-cover object-center"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Team Experience */}
      <section className="py-20 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="font-accent text-base tracking-[0.3em] text-[#7000FF] uppercase">Industry Professionals</span>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold uppercase mt-4 mb-4">
              OUR TEAM'S <span className="text-[#FF007F]">EXPERIENCE</span>
            </h2>
            <p className="text-white/60 max-w-2xl mx-auto">
              The UPA team have an amazing spectrum of experience within the industry. 
              You will not find another school in the local area with such a wealth of talent.
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-4">
            {teamExperience.map((exp, idx) => (
              <Badge 
                key={idx} 
                className="px-6 py-3 text-sm bg-[#111] border border-[#333] text-white hover:border-[#FF007F]/50 transition-colors"
              >
                {exp}
              </Badge>
            ))}
          </div>

          <div className="mt-12 text-center">
            <p className="text-white/60 mb-6">
              Plus, access to top West End and TV stars for masterclasses and workshops!
            </p>
            <Button 
              className="bg-[#FF007F] hover:bg-[#D6006A] text-white font-bold uppercase tracking-wider px-8 py-6 rounded-none"
              onClick={() => navigate('/classes')}
            >
              Explore Our Classes <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* Free Trial CTA Banner */}
      <FreeTrialBanner variant="dark" />

      {/* Video Section - See UPA in Action */}
      <section className="py-20 bg-[#050505]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="font-accent text-base tracking-[0.3em] text-[#7000FF] uppercase">Watch</span>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold uppercase mt-4">
              SEE UPA <span className="text-[#FF007F]">IN ACTION</span>
            </h2>
          </div>
          
          <div className="relative rounded-xl overflow-hidden border border-[#333] aspect-video group">
            {!isVideoPlaying ? (
              <>
                <img 
                  src="https://customer-assets.emergentagent.com/job_class-manager-84/artifacts/4is8revz_040.JPG"
                  alt="UPA Video Thumbnail"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                  <button
                    onClick={handlePlayVideo}
                    className="w-20 h-20 bg-[#FF007F] rounded-full flex items-center justify-center hover:bg-[#D6006A] transition-colors group-hover:scale-110 transform duration-300"
                    data-testid="play-video-btn"
                  >
                    <Play className="w-8 h-8 text-white ml-1" />
                  </button>
                </div>
              </>
            ) : (
              <video
                ref={videoRef}
                autoPlay
                controls
                className="w-full h-full object-cover"
                poster="https://customer-assets.emergentagent.com/job_class-manager-84/artifacts/4is8revz_040.JPG"
              >
                <source src="https://customer-assets.emergentagent.com/job_1d578f26-1f3f-468c-870b-210440301216/artifacts/dalqb9go_UPA%20ad%20small.MOV.mov" type="video/quicktime" />
                <source src="https://customer-assets.emergentagent.com/job_1d578f26-1f3f-468c-870b-210440301216/artifacts/dalqb9go_UPA%20ad%20small.MOV.mov" type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            )}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 lg:px-12 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-[#7000FF] to-[#FF007F]" />
        <div className="absolute inset-0 bg-[url('https://customer-assets.emergentagent.com/job_class-manager-84/artifacts/4is8revz_040.JPG')] bg-cover bg-center opacity-20" />
        
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold uppercase mb-6">
            READY TO JOIN UPA?
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Take your first step towards becoming a performer with Universal Performing Arts.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button 
              size="lg" 
              className="bg-white text-[#FF007F] hover:bg-white/90 font-bold uppercase tracking-wider px-10 py-6 rounded-none"
              onClick={() => navigate('/free-trial')}
            >
              BOOK FREE TRIAL
            </Button>
            <Button 
              size="lg" 
              variant="outline"
              className="border-2 border-white text-white hover:bg-white/10 font-bold uppercase tracking-wider px-10 py-6 rounded-none"
              onClick={() => navigate('/classes')}
            >
              VIEW CLASSES
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
